import React, { useEffect, useMemo, useState } from 'react';
import { IStorageUnit } from '../../@types/model/masterData/storageUnit/storageUnit';
import { PackmanLabel } from '../../components/label/PackmanLabel';
import { useAppSelector } from '../../@types/redux';
import { BooleanFlag, formatDateTime } from '@zz2/zz2-ui';
import { Card } from '@mui/material';
import { CustomTable } from '../../components/table/CustomTableWrapper';
import { IColdStorage } from '../../@types/model/coldStorage/coldStorage';
import ColdStorageHttpService from '../../services/http/coldStorage/coldStorageHttpService';
import { generalShowErrorSnackbar } from '../../store/general/Functions';
import { IApiError } from '../../@types/other';
import moment from 'moment';

interface IColdStorageSummaryProps {
    isLoading : boolean;
    selectedStorageUnit : IStorageUnit;
    setIsLoading : (value : boolean) => void;
}

const ColdStorageSummary = (props : IColdStorageSummaryProps) => {
    const sites = useAppSelector(state => state.masterData.sites);

    const [coldStorageList, setColdStorageList] = useState<Array<IColdStorage>>([]);


    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        loadData();
    }, [props.selectedStorageUnit]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/
    
    const loadData = async () => {
        try {
            props.setIsLoading(true);
            const res = await ColdStorageHttpService.GetList([props.selectedStorageUnit.id]);
            setColdStorageList(res.data);
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading cold storage data.');
        } finally {
            props.setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const getSiteDescription = (siteId : number) : string => {
        const site = sites.find(x => x.id === siteId);
        return site?.description ?? '';
    };

    const getExpectedDurationHours = (seconds : number) : string => {
        const milliseconds = moment.duration(seconds, 'seconds').asMilliseconds();

        return moment.utc(milliseconds).format('HH:mm:ss');
    };

    const getActualDurationHours = (id : number, row : IColdStorage) : string => {
        if (!row.endedOn) return ' - ';

        const startDateTime = moment.utc(row.startedOn).local();
        const endDateTime = moment.utc(row.endedOn).local();
        const diffInSeconds = endDateTime.diff(startDateTime, 'seconds');
        const milliseconds = moment.duration(diffInSeconds, 'seconds').asMilliseconds();

        return moment.utc(milliseconds).format('HH:mm:ss');
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const filteredColdStorageList = useMemo(() => {
        return coldStorageList
            .filter(x => x.storageUnitId === props.selectedStorageUnit.id)
            .sort((a, b) => b.id - a.id);
    }, [props.selectedStorageUnit.id, coldStorageList]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className={'fdc flx1 p20'}>
            <div className={'fdr pl10 pr10 pb10'}>
                <PackmanLabel
                    label={'Code'}
                    value={props.selectedStorageUnit.code}
                />
                <PackmanLabel
                    label={'Description'}
                    value={props.selectedStorageUnit.description}
                />
                <PackmanLabel
                    label={'Site'}
                    value={getSiteDescription(props.selectedStorageUnit.siteId)}
                />
            </div>
            <div className={'fdr p10'}>
                <BooleanFlag
                    label={'Tippable?'}
                    value={props.selectedStorageUnit.isTippable}
                />
                <BooleanFlag
                    label={'Has Raw Stock?'}
                    value={props.selectedStorageUnit.hasRawStock}
                />
                <BooleanFlag
                    label={'Has Packed Stock?'}
                    value={props.selectedStorageUnit.hasPackedStock}
                />
            </div>
            <Card className={'fdc hfill'}>
                <CustomTable<IColdStorage>
                    enableSorting
                    enableFiltering
                    columns={[
                        { title: 'Started On', field: 'startedOn', type: 'dateTime', formatFunction: formatDateTime, flex: 1, enableFiltering: true, enableSorting: true },
                        { title: 'Expected Run Time', field: 'expectedRunTimeSeconds', formatFunction: getExpectedDurationHours, width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Ended On', field: 'endedOn', type: 'dateTime', formatFunction: formatDateTime, flex: 1, enableFiltering: true, enableSorting: true },
                        { title: 'Actual Run Time', field: 'id', formatFunction: getActualDurationHours, width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, flex: 1, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, flex: 1, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, flex: 1, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, flex: 1, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={filteredColdStorageList}
                    initialSortOrder={[{ columnName: 'id', direction: 'desc' }]}
                    isActive={row => row.isActive}
                />
            </Card>
        </div>
    );
};

export default ColdStorageSummary;